import { AfterViewChecked, AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Data, Router } from '@angular/router';
import { FilterMetadata } from 'primeng/api';
import { MultiSelect } from 'primeng/multiselect';
import { Table } from 'primeng/table';
import { ObjectUtils } from 'primeng/utils';
import { BehaviorSubject, Observable, Subject, takeUntil, tap } from 'rxjs';
import { PreviousRouteService } from 'src/app/services/previous-route.service';
import { AuthService } from '../auth/auth.service';
import { AuthUserType } from '../auth/auth.types';
import { CrimeGunService } from '../services/crime-gun.service';
import { EventService } from '../services/event.service';
import { FilterService } from '../services/filter.service';
import { LookupService } from '../services/lookup.service';
import { AppConstants } from '../shared/app.constants';
import { AppPermissions } from '../shared/app.permissions';
import { DateRangeFilterComponent } from '../shared/components/date-range-filter/date-range-filter.component';
import { GeographicFilterComponent } from '../shared/components/geographic-filter/geographic-filter.component';
import { IncludeCDSFilterComponent } from '../shared/components/include-cds-agencies/include-cds-agencies-filter.component';
import { LinkedAgencyFilterComponent } from '../shared/components/linked-agency-filter/linked-agency-filter.component';
import { DateRangeFilterModel } from '../shared/models/date-range-filter-model';
import { GeographicFilterModel } from '../shared/models/geographic-filter-model';
import { FiltersViewModel, TableLazyLoadEventExt, TableStateExt } from '../shared/models/primeng-ext';


@Component({
  selector: 'ness-crime-guns',
  templateUrl: './crime-guns.component.html'
})
export class CrimeGunsComponent implements OnInit, OnDestroy, AfterViewInit, AfterViewChecked {
  cols: any[] = [
    { field: 'CrimeGunNewDataTypesName', header: 'New Data' },
    { field: 'CrimeGunTriggerDate', header: 'Date CGID Created/Updated' },
    { field: 'TriageLevelName', header: (this.authService.hasUserType(['StateLocalLE']) && !this.authService.hasRole(['NESS Administrator']) ? 'My Agency CGID Triage/Review' : 'ATF CGID Triage/Review') },
    { field: 'CrimeGunNumber', header: 'Crime Gun ID' },
    { field: 'LegacyCGICGunID', header: 'Legacy Gun ID(s)' },
    { field: 'SiteCode', header: 'Most Recent NIBIN Site' },
    { field: 'LabCaseNumber', header: 'Most Recent NIBIN Case #' },
    { field: 'AgencyName', subfield: 'AgencyAbbreviation', hoverfield: 'AgencyName', header: 'LE Agency' },
    { field: 'LECaseNumber', header: 'Most Recent LE Case #' },
    { field: 'CrimeTypeName', subfield: 'CrimeTypeName', header: 'NIBIN Crime Type' },
    { field: 'GunRecovered', header: 'Recovered' },
    { field: 'CountofPOE', header: '# Linked Shootings' },
    { field: 'MostRecentLeadDate', header: 'NIBIN Lead Date' }
  ];
  crimeGunNewDataTypes$: Observable<ServerModels.ENESS_REF_CrimeGunNewDataTypes[]>;
  crimeTypes$: Observable<ServerModels.ENESS_REF_CrimeTypes[]>;
  event$: Observable<ServerModels.EventDataModel>;
  loadingEndRow: number = AppConstants.dbPagingDefaultRows;
  loadingStartRow: number = 1;
  loadingStartRow$: BehaviorSubject<number> = new BehaviorSubject<number>(this.loadingStartRow);
  loadingEndRow$: BehaviorSubject<number> = new BehaviorSubject<number>(this.loadingEndRow);
  crimeTypeName: string;
  crimeTypes: ServerModels.ENESS_REF_CrimeTypes[];
  event: ServerModels.EventDataModel;
  dateFilterModel: DateRangeFilterModel = null;
  geographicFilter: GeographicFilterModel = null;
  includeCDSFilter: boolean;
  gunCases: ServerModels.HomePageView[] = [];
  loading: boolean = false;
  messageTemplate: string;
  rows: number = AppConstants.defaultTableRows;
  totalRecords: number = 0;
  showEventDetail: boolean = false;
  showManageAlertSidebar: boolean = false;
  showManageAlertForm: boolean = false;
  showSidebar: boolean = false;
  triageLevels: ServerModels.ENESS_REF_TriageTierLevel[] = [];
  newDataTooltipText: string = null;
  minSelectionLimit: number = 5;
  minSelectionItemReached: boolean;
  readonly isATFUserCreateAlert = AppPermissions.canCreateCrimeGunsCaseAlerts;
  readonly isSLUserCreateAlert = AppPermissions.canViewSLFilter;
  readonly canViewLinkedAgencies = AppPermissions.canViewLinkedAgencies;
  readonly canViewGeoFilters = AppPermissions.canViewCrimeGunsList;
  readonly hasUserType: AuthUserType[] = ['ATFFieldDivision', 'ATFHQ'];
  readonly isCDSGuest = this.authService.isCDSGuest();
  readonly gridStateKey: string = 'crimeGunGridState'
  readonly crimeGunStorageKey: string = 'crimeGunFilterModel';
  readonly geoFilterStorageKey: string = 'geoFilterStorageKeyCrimeGuns';
  readonly linkedAgencyStorageKey: string = 'linkedAgenciesStKeyCrimeGuns';
  readonly includeCDSStorageKey: string = 'includeCDSKeyCrimeGuns';
  @ViewChild('df', { static: false }) dateRangeFilterCmp: DateRangeFilterComponent;
  @ViewChild('gf', { static: false }) geoFilterCmp: GeographicFilterComponent;
  @ViewChild('af', { static: false }) linkedAgencyFilterCmp: LinkedAgencyFilterComponent;
  @ViewChild('cf', { static: false }) includeCDSFilterCmp: IncludeCDSFilterComponent;
  @ViewChild('dt', { static: false }) dataTable: Table;
  @ViewChild('colselect', { static: false }) columnMultiSelect: MultiSelect;
  private _unsubscribe$: Subject<void> = new Subject<void>();
  private lastTableLazyLoadEvent: TableLazyLoadEventExt;
  private isPageLoading: boolean = true;
  private isStateLocalUser: boolean = false;
  private _selectedColumns: any[];
  get selectedColumns(): any[] {
    return this._selectedColumns;
  }
  public get cdsVirtualGroups(): ServerModels.VirtualGroupViewModel[] {
    return this.authService.cdsVirtualGroups;
  }
  set selectedColumns(selectedCols: any[]) {
    this._selectedColumns = this.cols.filter((col) => selectedCols.some(x => x.field == col.field));
    this.dataTable?.saveState();
  }

  constructor(public router: Router,
    public lookupService: LookupService,
    public filterService: FilterService,
    private authService: AuthService,
    private crimeGunService: CrimeGunService,
    private eventService: EventService,
    private previousRouteService: PreviousRouteService,
    private changeDetectorRef: ChangeDetectorRef) { }

  ngOnInit() {
    let tableState: TableStateExt = this.getTableState();
    if (!tableState['selectedColumns']) {
      this._selectedColumns = this.cols;
    }

    this.messageTemplate = 'Loading crime guns...';
    if (!this.authService.hasRole(['NESS Administrator']) && this.authService.hasUserType(['StateLocalLE'])) {
      this.filterService.geographicFilterModel$.next(null);
    }

    this.crimeGunNewDataTypes$ = this.crimeGunService.getCrimeGunNewDataTypes();

    this.crimeTypes$ = this.lookupService.getAllCrimeTypes().pipe(tap((crimeType: ServerModels.ENESS_REF_CrimeTypes[]) => {
      this.crimeTypes = crimeType;
    }));

    this.newDataTooltipText = this.authService.hasUserType(['StateLocalLE']) ? 'New NIBIN or Trace Data Processed into NESS.' : 'New NIBIN or Trace data processed in NESS. The Trace data is filtered by only Crime Gun IDs with a time to first shooting under 2 years (TTFS < 2 years).';

    this.previousRouteService.returnUrl = this.router.url;
    this.previousRouteService.transferData = { titleText: 'Crime Guns List' } as Data;

  }

  ngOnDestroy() {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  ngAfterViewInit(): void {
    this.columnMultiSelect.isOptionDisabled = (option: any) => {
      let disabled = this.columnMultiSelect.optionDisabled ? ObjectUtils.resolveFieldData(option, this.columnMultiSelect.optionDisabled) : (option.disabled !== undefined ? option.disabled : false);
      return (disabled || (this.minSelectionItemReached && this.columnMultiSelect.isSelected(option)));
    };

    this.isPageLoading = false;
    this.applyFilters(false);
  }

  ngAfterViewChecked(): void {
    this.changeDetectorRef.detectChanges();
  }


  public loadPage(lazyLoadEvent: TableLazyLoadEventExt) {
    this.lastTableLazyLoadEvent = lazyLoadEvent;
    if (this.isPageLoading) {
      return; // short-circuit to avoid null, page/filters are not ready yet
    }

    //lazyLoadEvent.first = First row offset
    //lazyLoadEvent.rows = Number of rows per page
    //lazyLoadEvent.sortField = Field name to sort in single sort mode
    //lazyLoadEvent.sortOrder = Sort order as number, 1 for asc and -1 for dec in single sort mode
    //multiSortMeta: An array of SortMeta objects used in multiple columns sorting. Each SortMeta has field and order properties.
    //filters: Filters object having field as key and filter value, filter matchMode as value
    //globalFilter: Value of the global filter if available
    this.loading = true;
    let filtersVM = {
      FieldDivisionID: '',
      FieldDivisionGroupIDs: [] as string[],
      AgencyIDs: [] as string[],
      StateID: '',
      CountyIDs: [] as string[],
      StartDate: this.dateFilterModel.startDate.toISOString(),
      EndDate: this.dateFilterModel.endDate.toISOString()
    } as FiltersViewModel;

    // Note: Use available value (not undefined/null)
    if (this.includeCDSFilter != undefined) {
      filtersVM.IncludeCDS = this.includeCDSFilter;
    }

    if (this.authService.hasRole(['NESS Administrator']) || this.authService.hasUserType(this.hasUserType)) {
      filtersVM.FieldDivisionID = this.geographicFilter.fieldDivisionID;
      filtersVM.FieldDivisionGroupIDs = this.geographicFilter.fieldDivisionGroups?.map(x => x.IntelligenceReportATFReferralGroupsID);
      filtersVM.StateID = this.geographicFilter.stateID;
      filtersVM.CountyIDs = this.geographicFilter.counties?.map(x => x.CountyID);
    }

    filtersVM.AgencyIDs = this.geographicFilter?.agencies?.map(x => x.AgencyID) || [];
    lazyLoadEvent.globalFilter = filtersVM;
    this.crimeGunService.getCrimeGunCasesPageResults(lazyLoadEvent).pipe(takeUntil(this._unsubscribe$)).subscribe({
      next: (resp: ServerModels.CrimeGunCasesPageResultsDTO) => {
        this.crimeGunService.loadingCrimeGunList = false;
        this.totalRecords = resp?.totalRecords;
        this.gunCases = resp?.hits;
        this.loading = false;
      },
      error: (err: any) => {
        this.loading = false;
        this.gunCases = [];
      }
    });
  }

  public applyFilters(isResetPage: boolean): void {
    if (isResetPage) {
      this.dataTable.first = 0;
      this.lastTableLazyLoadEvent.first = 0;
    }
    this.dateRangeFilterCmp.applyFilters();
    let storedDateFilter = JSON.parse(sessionStorage.getItem(this.crimeGunStorageKey));
    this.dateFilterModel = new DateRangeFilterModel(storedDateFilter['_fieldDivisionID'],
      storedDateFilter['_duration'],
      storedDateFilter['_startDate'],
      storedDateFilter['_endDate']);

    if (this.authService.hasRole(['NESS Administrator']) || this.authService.hasUserType(this.hasUserType)) {
      this.geoFilterCmp.applyFilters();
    } else {
      if (this.filterService.hasLinkedAgencies) {
        this.linkedAgencyFilterCmp.applyFilters();
      }
    }

    let storedGeoFilterModel = this.filterService.hasLinkedAgencies ? JSON.parse(sessionStorage.getItem(this.linkedAgencyStorageKey)) : JSON.parse(sessionStorage.getItem(this.geoFilterStorageKey));
    if (!!storedGeoFilterModel && Object.keys(storedGeoFilterModel).length > 0) {
      this.geographicFilter = new GeographicFilterModel(storedGeoFilterModel['_fieldDivisionID'],
        storedGeoFilterModel['_fieldDivisionGroupID'],
        storedGeoFilterModel['_fieldDivisionGroups'],
        storedGeoFilterModel['_stateID'],
        storedGeoFilterModel['_countyID'],
        storedGeoFilterModel['_counties'],
        storedGeoFilterModel['_agencyID'],
        storedGeoFilterModel['_agencies'],
        storedGeoFilterModel['_fieldDivisionName'],
        storedGeoFilterModel['_groupName'],
        storedGeoFilterModel['_groupNameList'],
        storedGeoFilterModel['_stateName'],
        storedGeoFilterModel['_countyName'],
        storedGeoFilterModel['_countyNameList'],
        storedGeoFilterModel['_agencyName'],
        storedGeoFilterModel['_agencyNamesList']
      );
    }

    // Note: HTML template may have not rendered this due to business rule
    if (this.includeCDSFilterCmp) {
      this.includeCDSFilterCmp.applyFilters();
      this.includeCDSFilter = JSON.parse(sessionStorage.getItem(this.includeCDSStorageKey));
    }
    else {
      // Business logic determined `!isCDSGuest`, so remove the session key value
      sessionStorage.removeItem(this.includeCDSStorageKey);
    }

    this.messageTemplate = 'Loading crime guns...';
    this.gunCases = [];
    this._clearLoadingStatus();
    this.loadPage(this.lastTableLazyLoadEvent);
  }

  public resetDefaultFilters(): void {
    if (!!this.geoFilterCmp && (this.authService.hasRole(['NESS Administrator']) || this.authService.hasUserType(this.hasUserType))) {
      this.geoFilterCmp.resetToDefaultFilters();
    } else {
      if (this.filterService.hasLinkedAgencies) {
        this.linkedAgencyFilterCmp.resetToDefaultFilters();
      }
    }

    this.dateRangeFilterCmp.resetToDefaultFilters();

    if (this.includeCDSFilterCmp) {
      this.includeCDSFilterCmp.resetToDefaultFilters();
    }
  }

  public viewEventDetails(selectedItem: ServerModels.HomePageView) {
    this.event$ = this.eventService.getEventById(selectedItem.EventID).pipe(tap({
      next: (event: ServerModels.EventDataModel) => {
        this.event = event;
      },
      complete: () => {
        this.showEventDetail = this.showSidebar = true;
      },
    }));
  }

  public displayLink(selectedItem: ServerModels.HomePageView) {
    if (this.isStateLocalUser) {
      let refOrganizationID = this.authService.organizationId ? this.authService.organizationId : this.authService.authOrgCode;

      // verify current user has access to the agency and it's linked agencies.
      if (selectedItem.EventID && refOrganizationID == selectedItem.EventVirtualGroupID)
        return true;
      else
        return false;

    } else {  // ATF or Field division user an view the event if event exists
      if (selectedItem.EventID)
        return true;
      else
        return false;
    }
  }

  private _clearLoadingStatus(): void {
    this.loadingStartRow = 1;
    this.loadingEndRow = AppConstants.dbPagingDefaultRows;
    this.loadingStartRow$.next(this.loadingStartRow);
    this.loadingEndRow$.next(this.loadingEndRow);
  }

  public getFilterValue(filter: any): any {
    if (!filter) {
      return null;
    }
    return (<FilterMetadata>filter).value;
  }

  public totalRecordsDisplay(): any {
    if (this.totalRecords < 10000)
      return "Found " + this.totalRecords.toLocaleString('en-US') + " Results:";
    else
      return "Found Top 10,000 Results:";
  }

  public onColumnSelectChange(event: { originalEvent: Event, value: any[], itemValue?: any }) {
    if (this.minSelectionLimit && (event.value && event.value.length === this.minSelectionLimit)) {
      this.minSelectionItemReached = true;
    }
    else {
      this.minSelectionItemReached = false;
    }
  }

  private getTableState(): TableStateExt {
    const storage = this.dataTable?.getStorage() || sessionStorage;
    const stateString = storage.getItem(this.dataTable?.stateKey || this.gridStateKey);
    const dateFormat = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/;
    const reviver = function (key: string, value: string) {
      if (dateFormat.test(value)) {
        return new Date(value);
      }

      return value;
    }

    let state: TableStateExt = (stateString) ? JSON.parse(stateString, reviver) : {};
    return state;
  }

  public saveTableState(state: TableStateExt): void {
    const storage = this.dataTable?.getStorage() || sessionStorage;
    state['selectedColumns'] = this._selectedColumns;
    storage.setItem(this.dataTable.stateKey, JSON.stringify(state));
  }

  public restoreState(state: TableStateExt): void {
    if (state['selectedColumns']) {
      this._selectedColumns = state['selectedColumns'];
    } else {
      this._selectedColumns = this.cols;
    }
  }

  public onManageAlertsClick() {
    this.showManageAlertForm = this.showSidebar = true;
  }

  public onCloseManageAlertsForm() {

    this.showManageAlertForm = this.showSidebar = false;
  }
}
